export const DUPLICATED_UPLOAD_OUTCOME = {
  SAME_ITEM: 'same-item',
  BAR_OPENED: 'bar-opened',
  LINKED: 'linked',
  LINK_FAILED: 'link-failed',
} as const;

export type DuplicatedUploadOutcome =
  (typeof DUPLICATED_UPLOAD_OUTCOME)[keyof typeof DUPLICATED_UPLOAD_OUTCOME];

type CreateLinkArgs = {
  categoryId: string;
  linkOwnerIds: string[];
  items: { id: string }[];
};

type ResolveDuplicatedUploadArgs = {
  duplicatedKnowledgeId: string;
  currentKnowledgeId: string | undefined;
  uploadedAssetId: string | undefined;
  categoryId: string | null | undefined;
  itemCategoryId: string | null | undefined;
  ownerId: string;
  createLink: (args: CreateLinkArgs) => Promise<{ error?: unknown } | unknown>;
  rejectDuplicatedItem: RejectDuplicatedItem;
};

type RejectDuplicatedItem = (args: { assetId: string | undefined }) => Promise<void>;

type HandleDuplicateDetectedArgs = {
  duplicatedKnowledgeId: string;
  currentKnowledgeId: string | undefined;
  uploadedAssetId: string | undefined;
  rejectDuplicatedItem: RejectDuplicatedItem;
  openDuplicatedBar: () => void;
};

// The item's own file is not a duplicate the user can act on, so the Link/Cancel bar never opens for it.
export async function handleDuplicateDetected({
  duplicatedKnowledgeId,
  currentKnowledgeId,
  uploadedAssetId,
  rejectDuplicatedItem,
  openDuplicatedBar,
}: HandleDuplicateDetectedArgs): Promise<DuplicatedUploadOutcome> {
  if (duplicatedKnowledgeId === currentKnowledgeId) {
    await rejectDuplicatedItem({ assetId: uploadedAssetId });
    return DUPLICATED_UPLOAD_OUTCOME.SAME_ITEM;
  }

  openDuplicatedBar();
  return DUPLICATED_UPLOAD_OUTCOME.BAR_OPENED;
}

// The duplicate upload never replaces the edited item: the backend rejects a `link`
// into /knowledge/items/, and an item whose link is its own page cannot be viewed.
export async function resolveDuplicatedUpload({
  duplicatedKnowledgeId,
  currentKnowledgeId,
  uploadedAssetId,
  categoryId,
  itemCategoryId,
  ownerId,
  createLink,
  rejectDuplicatedItem,
}: ResolveDuplicatedUploadArgs): Promise<DuplicatedUploadOutcome> {
  if (duplicatedKnowledgeId === currentKnowledgeId) {
    await rejectDuplicatedItem({ assetId: uploadedAssetId });
    return DUPLICATED_UPLOAD_OUTCOME.SAME_ITEM;
  }

  // The categoryId prop is only set when the editor opens from a binder. An existing item
  // opened directly has none, and the API strips the item's parentId, so the binder that
  // lists the item in the knowledge tree is the link target.
  const targetCategoryId = categoryId || itemCategoryId;
  if (!targetCategoryId) {
    await rejectDuplicatedItem({ assetId: uploadedAssetId });
    return DUPLICATED_UPLOAD_OUTCOME.LINK_FAILED;
  }

  const res = await createLink({
    categoryId: targetCategoryId,
    linkOwnerIds: [ownerId],
    items: [{ id: duplicatedKnowledgeId }],
  });

  await rejectDuplicatedItem({ assetId: uploadedAssetId });

  const failed = typeof res === 'object' && res !== null && 'error' in res && !!res.error;
  return failed ? DUPLICATED_UPLOAD_OUTCOME.LINK_FAILED : DUPLICATED_UPLOAD_OUTCOME.LINKED;
}
